import {
  DEMO_RECEIPT,
  InferenceError,
  type Category,
  type InferenceProvider,
  type PersonId,
  type Receipt,
  type ReceiptInput,
  type SplitContext,
  type SplitOp,
  type SplitReply,
} from '@bill/core';

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Words that stand in for a category in casual speech. */
const CATEGORY_WORDS: Array<[Category, RegExp]> = [
  ['alcohol', /\b(alcohol|booze|beers?|wine|drinks)\b/],
  ['tobacco', /\b(tobacco|cigarettes?|smokes)\b/],
  ['meat', /\b(meat|steaks?|ribs)\b/],
  ['drink', /\b(soft drinks?|sodas?|cola|coke)\b/],
  ['dessert', /\b(desserts?|cake)\b/],
  ['food', /\b(food)\b/],
];

/**
 * A provider that needs no key and no network.
 *
 * Any photo becomes the fixture receipt from idea.md, and the split is a tiny
 * rule-based reading of the sentence — enough to walk every screen, including
 * the clarifying questions, without spending a real request. It is not a
 * model and does not pretend to be one; the point is that the whole UI can be
 * built and demoed offline.
 */
export class DemoProvider implements InferenceProvider {
  readonly kind = 'mock' as const;

  async structureReceipt(_input: ReceiptInput, signal?: AbortSignal): Promise<Receipt> {
    await wait(900);
    if (signal?.aborted) throw new InferenceError('cancelled');
    return structuredClone(DEMO_RECEIPT);
  }

  async parseSplit(context: SplitContext, utterance: string): Promise<SplitReply> {
    await wait(600);
    const ops: SplitOp[] = [];
    const everyone = { kind: 'allExcept' as const, ids: [] as PersonId[] };

    // People the sentence introduces: capitalised words that are not known
    // names and not sentence-initial. Good enough to walk the screens.
    const known = new Set(context.people.map((person) => person.name.toLowerCase()));
    const declared = new Map<string, string>();
    for (const match of utterance.matchAll(/(?<=[^.!?]\s|^)([A-ZĂÂÎȘȚ][\p{L}]+)/gu)) {
      const word = match[1] ?? '';
      if (/^(I|We|Me|Everyone|Everybody|The|And|Then|Actually|Split|All)$/i.test(word)) continue;
      if (known.has(word.toLowerCase()) || declared.has(word.toLowerCase())) continue;
      if (context.items.some((item) => item.name.toLowerCase().includes(word.toLowerCase()))) continue;
      declared.set(word.toLowerCase(), `n${declared.size + 1}`);
    }
    const people = [...declared].map(([name, id]) => ({ id, name: name[0]?.toUpperCase() + name.slice(1) }));
    const idOf = (name: string): PersonId | undefined =>
      context.people.find((person) => person.name.toLowerCase() === name)?.id ?? declared.get(name);
    const countWords: Record<string, number> = { two: 2, three: 3, four: 4, five: 5, six: 6, doi: 2, trei: 3, patru: 4, cinci: 5 };
    const sizeMatch = /(?:we (?:are|were)|there (?:are|were)|the) (\d+|two|three|four|five|six|doi|trei|patru|cinci)(?: of us)?/i.exec(utterance) ?? /am fost (\d+|doi|trei|patru|cinci)/i.exec(utterance);
    const groupSize = sizeMatch ? (Number.parseInt(sizeMatch[1] ?? '', 10) || countWords[(sizeMatch[1] ?? '').toLowerCase()]) : undefined;
    const usesEveryone = /\b(everyone|everybody|all of us|we all|the rest of us|toți|toti|all)\b/i.test(utterance);

    // Each comma- or "and"-separated clause is read on its own so that
    // "Alex and me split the beer, Jason had the cigarettes" gives two ops.
    const clauses = utterance
      .toLowerCase()
      .split(/[,.;]|\bbut\b|\band then\b/)
      .map((clause) => clause.trim())
      .filter(Boolean);

    for (const clause of clauses) {
      const named = [...context.people.map((person) => person.name.toLowerCase()), ...declared.keys()]
        .filter((name) => new RegExp(`\\b${name}\\b`).test(clause))
        .map((name) => idOf(name))
        .filter((id): id is PersonId => id !== undefined);
      if (context.speakerId && /\b(me|i|myself)\b/.test(clause) && !named.includes(context.speakerId)) {
        named.push(context.speakerId);
      }
      const excluded = /\b(except|minus|but not|without)\b/.test(clause);
      const to =
        named.length === 0 ? everyone : excluded ? { kind: 'allExcept' as const, ids: named } : { kind: 'only' as const, ids: named };

      const category = CATEGORY_WORDS.find(([, pattern]) => pattern.test(clause))?.[0];
      if (category) {
        ops.push({ kind: 'assign', items: { kind: 'category', category }, to });
        continue;
      }

      const byName = context.items.filter((item) => clause.includes(item.name.toLowerCase()));
      if (byName.length > 0) {
        ops.push({ kind: 'assign', items: { kind: 'ids', ids: byName.map((item) => item.id) }, to });
        continue;
      }

      if (/\b(everything|every ?one|all|rest|equally|evenly|share|split)\b/.test(clause)) {
        ops.push({ kind: 'assign', items: { kind: 'rest' }, to });
      }
    }

    // "rest" clauses must run last so they see what the named clauses claimed.
    return {
      people,
      ...(groupSize ? { groupSize } : {}),
      everyone: usesEveryone,
      ops: [
        ...ops.filter((op) => op.items.kind !== 'rest'),
        ...ops.filter((op) => op.items.kind === 'rest'),
      ],
    };
  }
}
